import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import OtterIcon from "@/components/app/OtterIcon";

export default function Login() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate("/app");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r border-border bg-foreground text-background">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="login-brand-link">
            <div className="w-7 h-7 bg-background grid place-items-center text-foreground">
              <OtterIcon size={18} />
            </div>
            <span className="font-semibold tracking-tight text-lg">ArloSEO</span>
          </Link>
          <div>
            <p className="font-mono-data text-xs uppercase tracking-widest text-background/60">// the brief</p>
            <h2 className="font-semibold tracking-tight text-5xl mt-4 max-w-md leading-[0.95]">
              Welcome back. Your rankings won&apos;t track themselves.
            </h2>
            <p className="mt-6 text-background/70 max-w-md leading-relaxed">
              Pick up where you left off — every audit, keyword and competitor exactly as you saw it last.
            </p>
          </div>
          <div className="font-mono-data text-xs text-background/40">SESSION · SECURE · HTTPONLY</div>
        </div>
      </div>

      <div className="p-8 lg:p-16 grid place-items-center bg-background">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground" data-testid="login-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back home
          </Link>
          <h1 className="font-semibold tracking-tight text-4xl mt-6">Sign in.</h1>
          <p className="text-sm text-muted-foreground mt-2">Use the admin demo or your own account.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono-data text-xs uppercase tracking-widest">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="font-mono-data text-xs uppercase tracking-widest">Password</Label>
                <Link to="/forgot-password" className="font-mono-data text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="login-forgot-link">
                  Forgot?
                </Link>
              </div>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="border border-destructive/40 bg-destructive/5 text-destructive text-sm px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full rounded-none h-11 bg-foreground hover:bg-foreground/90" data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in →"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            No account?{" "}
            <Link to="/register" className="underline underline-offset-4 text-foreground" data-testid="login-register-link">
              Create one
            </Link>
          </p>
          <div className="mt-10 border border-border p-4 font-mono-data text-xs">
            <div className="text-muted-foreground uppercase tracking-widest mb-2">demo</div>
            <div>admin@arloseo.com / admin123</div>
          </div>
        </div>
      </div>
    </div>
  );
}
